import { useState, useEffect } from 'react';
import { getAllRoutes, FerryRoute } from '@/lib/ferryRoutes';
import { edgeFn } from '@/lib/edgeFn';

const CACHE_KEY = 'ferry_routes_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000;

export interface UseFerryRoutesResult {
  routes: FerryRoute[];
  isLoading: boolean;
  isFromFallback: boolean;
  /** True when the network fetch failed and no usable cache was available. */
  fetchFailed: boolean;
}

export function useFerryRoutes(): UseFerryRoutesResult {
  const [routes, setRoutes] = useState<FerryRoute[]>(
    () => getAllRoutes()
  );
  // Start loading so consumers do not treat the empty bootstrap list as "not found".
  const [isLoading, setIsLoading] = useState(true);
  const [isFromFallback, setIsFromFallback] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Date.now() - cached.cachedAt < CACHE_TTL && cached.routes?.length > 0) {
          setRoutes(cached.routes);
          setIsFromFallback(false);
          setFetchFailed(false);
          setIsLoading(false);
          return;
        }
      }
    } catch { /* intentionally empty */ }

    setIsLoading(true);
    setFetchFailed(false);
    edgeFn<{ routes?: FerryRoute[] }>('ferry-routes-all', { method: 'GET' })
      .then((data) => {
        if ((data?.routes?.length ?? 0) > 0) {
          setRoutes(data.routes as FerryRoute[]);
          setIsFromFallback(false);
          setFetchFailed(false);
          try {
            sessionStorage.setItem(CACHE_KEY,
              JSON.stringify({
                routes: data.routes,
                cachedAt: Date.now(),
              })
            );
          } catch { /* intentionally empty */ }
        } else {
          // Empty successful payload — keep fallback stub, mark as failed for soft-404 logic.
          setIsFromFallback(true);
          setFetchFailed(true);
        }
      })
      .catch(() => {
        setIsFromFallback(true);
        setFetchFailed(true);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { routes, isLoading, isFromFallback, fetchFailed };
}
